import BarChartOutlined from "@mui/icons-material/BarChartOutlined";
import CampaignOutlined from "@mui/icons-material/CampaignOutlined";
import CookieOutlined from "@mui/icons-material/CookieOutlined";
import OndemandVideoOutlined from "@mui/icons-material/OndemandVideoOutlined";
import ShieldOutlined from "@mui/icons-material/ShieldOutlined";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Link from "@mui/material/Link";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import React from "react";
import {
  COOKIE_CONSENT_OPEN_EVENT,
  COOKIE_CONSENT_STORAGE_KEY,
  CookieConsentSelection,
  enableConsentedServices,
  getCookieConsent,
  removeTrackingCookies,
  requiresReloadAfterConsentChange,
  saveCookieConsent,
} from "../../util/cookieConsent";
import { Banner, classes, PreferencesDialog } from "./styles";

const DEFAULT_SELECTION: CookieConsentSelection = {
  analytics: false,
  marketing: false,
  externalMedia: false,
};

const CookieConsent = () => {
  const [isReady, setIsReady] = React.useState(false);
  const [showBanner, setShowBanner] = React.useState(false);
  const [showPreferences, setShowPreferences] = React.useState(false);
  const [selection, setSelection] =
    React.useState<CookieConsentSelection>(DEFAULT_SELECTION);

  React.useEffect(() => {
    const storedConsent = getCookieConsent();
    const currentUrl = new URL(window.location.href);
    const isReadingPolicy =
      currentUrl.searchParams.get("cookie-notice") === "read";

    if (isReadingPolicy) {
      currentUrl.searchParams.delete("cookie-notice");
      window.history.replaceState(
        window.history.state,
        "",
        currentUrl.pathname + currentUrl.search + currentUrl.hash
      );
    }

    if (storedConsent) {
      const storedSelection = {
        analytics: storedConsent.analytics,
        marketing: storedConsent.marketing,
        externalMedia: storedConsent.externalMedia,
      };
      setSelection(storedSelection);
      enableConsentedServices(storedSelection);
    } else if (!isReadingPolicy) {
      setShowBanner(true);
    }
    setIsReady(true);

    const handleOpenPreferences = () => {
      const consent = getCookieConsent();
      setSelection(
        consent
          ? {
              analytics: consent.analytics,
              marketing: consent.marketing,
              externalMedia: consent.externalMedia,
            }
          : DEFAULT_SELECTION
      );
      setShowPreferences(true);
    };

    const handleConsentFromAnotherTab = (event: StorageEvent) => {
      if (event.key === COOKIE_CONSENT_STORAGE_KEY) {
        window.location.reload();
      }
    };

    window.addEventListener(COOKIE_CONSENT_OPEN_EVENT, handleOpenPreferences);
    window.addEventListener("storage", handleConsentFromAnotherTab);
    return () => {
      window.removeEventListener(
        COOKIE_CONSENT_OPEN_EVENT,
        handleOpenPreferences
      );
      window.removeEventListener("storage", handleConsentFromAnotherTab);
    };
  }, []);

  const applySelection = (nextSelection: CookieConsentSelection) => {
    const previousConsent = getCookieConsent();
    const mustReload = requiresReloadAfterConsentChange(
      previousConsent,
      nextSelection
    );

    saveCookieConsent(nextSelection);
    setSelection(nextSelection);
    setShowBanner(false);
    setShowPreferences(false);

    if (mustReload) {
      removeTrackingCookies();
      window.location.reload();
      return;
    }

    enableConsentedServices(nextSelection);
  };

  const acceptAll = () =>
    applySelection({
      analytics: true,
      marketing: true,
      externalMedia: true,
    });
  const rejectAll = () => applySelection(DEFAULT_SELECTION);
  const closePreferences = () => {
    setShowPreferences(false);
    if (!getCookieConsent()) {
      setShowBanner(true);
    }
  };

  if (!isReady) {
    return null;
  }

  return (
    <React.Fragment>
      {showBanner && (
        <Banner
          open
          disableAutoFocus
          BackdropProps={{ invisible: true }}
          aria-labelledby="cookie-banner-title"
        >
          <div className={classes.bannerInner}>
            <div className={classes.bannerCopy}>
              <CookieOutlined className={classes.bannerIcon} />
              <div>
                <Typography
                  id="cookie-banner-title"
                  variant="h6"
                  component="h2"
                  gutterBottom
                >
                  Vos données, votre choix
                </Typography>
                <Typography variant="body2">
                  Nous utilisons des traceurs pour mesurer l’audience du site et
                  les conversions LinkedIn. Vous pouvez accepter, refuser ou
                  choisir par finalité. Les traceurs nécessaires restent actifs.{" "}
                  <Link
                    className={classes.policyLink}
                    href="/politique-confidentialite/?cookie-notice=read"
                    underline="always"
                  >
                    En savoir plus
                  </Link>
                </Typography>
              </div>
            </div>
            <div className={classes.bannerActions}>
              <Button
                className={classes.actionButton}
                color="innoBlue"
                variant="contained"
                onClick={rejectAll}
              >
                Tout refuser
              </Button>
              <Button
                className={classes.actionButton}
                color="innoBlue"
                variant="outlined"
                onClick={() => {
                  setShowBanner(false);
                  setShowPreferences(true);
                }}
              >
                Personnaliser
              </Button>
              <Button
                className={classes.actionButton}
                color="innoBlue"
                variant="contained"
                onClick={acceptAll}
              >
                Tout accepter
              </Button>
            </div>
          </div>
        </Banner>
      )}

      <PreferencesDialog
        open={showPreferences}
        onClose={closePreferences}
        aria-labelledby="cookie-preferences-title"
      >
        <DialogTitle id="cookie-preferences-title">
          Vos préférences de confidentialité
        </DialogTitle>
        <DialogContent>
          <Typography className={classes.dialogIntro} variant="body2">
            Choisissez les finalités que vous autorisez. Votre choix est
            conservé pendant six mois et peut être modifié à tout moment via le
            lien « Gérer mes cookies » en bas du site.
          </Typography>

          <div className={classes.categoryList}>
            <div className={classes.category}>
              <div className={classes.categoryHeader}>
                <ShieldOutlined className={classes.categoryIcon} />
                <div className={classes.categoryCopy}>
                  <Typography variant="subtitle1" component="h3">
                    Nécessaires
                  </Typography>
                  <Typography variant="body2">
                    Indispensables au fonctionnement du site et à la
                    mémorisation de votre choix.
                  </Typography>
                  <Typography
                    className={classes.categoryMeta}
                    variant="caption"
                  >
                    Innovance Solutions · Toujours actifs
                  </Typography>
                </div>
                <Switch
                  checked
                  disabled
                  inputProps={{ "aria-label": "Traceurs nécessaires actifs" }}
                />
              </div>
            </div>

            <div className={classes.category}>
              <div className={classes.categoryHeader}>
                <BarChartOutlined className={classes.categoryIcon} />
                <div className={classes.categoryCopy}>
                  <Typography variant="subtitle1" component="h3">
                    Mesure d’audience
                  </Typography>
                  <Typography variant="body2">
                    Nous aide à comprendre les pages consultées et à améliorer
                    le site.
                  </Typography>
                  <Typography
                    className={classes.categoryMeta}
                    variant="caption"
                  >
                    Google Analytics 4 · Déclenché après accord
                  </Typography>
                </div>
                <Switch
                  checked={selection.analytics}
                  color="innoBlue"
                  onChange={(event) =>
                    setSelection({
                      ...selection,
                      analytics: event.target.checked,
                    })
                  }
                  inputProps={{
                    "aria-label": "Autoriser la mesure d’audience",
                  }}
                />
              </div>
            </div>

            <div className={classes.category}>
              <div className={classes.categoryHeader}>
                <CampaignOutlined className={classes.categoryIcon} />
                <div className={classes.categoryCopy}>
                  <Typography variant="subtitle1" component="h3">
                    Mesure des campagnes
                  </Typography>
                  <Typography variant="body2">
                    Mesure l’efficacité de nos campagnes et conversions
                    publicitaires LinkedIn.
                  </Typography>
                  <Typography
                    className={classes.categoryMeta}
                    variant="caption"
                  >
                    LinkedIn Insight · Déclenché après accord
                  </Typography>
                </div>
                <Switch
                  checked={selection.marketing}
                  color="innoBlue"
                  onChange={(event) =>
                    setSelection({
                      ...selection,
                      marketing: event.target.checked,
                    })
                  }
                  inputProps={{
                    "aria-label": "Autoriser la mesure des campagnes",
                  }}
                />
              </div>
            </div>

            <div className={classes.category}>
              <div className={classes.categoryHeader}>
                <OndemandVideoOutlined className={classes.categoryIcon} />
                <div className={classes.categoryCopy}>
                  <Typography variant="subtitle1" component="h3">
                    Contenus externes
                  </Typography>
                  <Typography variant="body2">
                    Autorise l’affichage des vidéos hébergées par des services
                    tiers.
                  </Typography>
                  <Typography
                    className={classes.categoryMeta}
                    variant="caption"
                  >
                    YouTube · Chargé uniquement après accord
                  </Typography>
                </div>
                <Switch
                  checked={selection.externalMedia}
                  color="innoBlue"
                  onChange={(event) =>
                    setSelection({
                      ...selection,
                      externalMedia: event.target.checked,
                    })
                  }
                  inputProps={{
                    "aria-label": "Autoriser les contenus externes",
                  }}
                />
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            className={classes.actionButton}
            color="innoBlue"
            variant="contained"
            onClick={rejectAll}
          >
            Tout refuser
          </Button>
          <Button
            className={classes.actionButton}
            color="innoBlue"
            variant="outlined"
            onClick={() => applySelection(selection)}
          >
            Enregistrer mes choix
          </Button>
          <Button
            className={classes.actionButton}
            color="innoBlue"
            variant="contained"
            onClick={acceptAll}
          >
            Tout accepter
          </Button>
        </DialogActions>
      </PreferencesDialog>
    </React.Fragment>
  );
};

export default CookieConsent;
